import { z } from 'zod';
import { RRule, rrulestr } from 'rrule';
import type { Options } from 'rrule';

export type RRuleOptions = Partial<Options>;

export interface RRuleInstance {
  /** The RRule configuration used to generate this instance */
  rule: RRuleOptions;
  /** Array of generated dates based on the rule */
  dates: Date[];
  /** The timezone used for date generation */
  timeZone: string;
  /** Optional end date for the recurrence */
  until?: Date;
  /** Optional count limit for the recurrence */
  count?: number;
}

export const rruleOptionsSchema = z.object({
  freq: z.number().min(0).max(6).optional(),
  dtstart: z.union([z.string().transform((val) => new Date(val)), z.date()]).optional(),
  interval: z.number().positive().optional(),
  wkst: z.number().min(0).max(6).optional(),
  count: z.number().positive().optional(),
  until: z.union([z.string().transform((val) => new Date(val)), z.date()]).optional(),
  tzid: z.string().optional(),
  bysetpos: z.array(z.number()).optional(),
  bymonth: z.array(z.number().min(1).max(12)).optional(),
  bymonthday: z
    .union([z.number().min(-31).max(31), z.array(z.number().min(-31).max(31))])
    .optional(),
  bynmonthday: z.array(z.number()).optional(),
  byyearday: z.array(z.number().min(-366).max(366)).optional(),
  byweekno: z.array(z.number().min(-53).max(53)).optional(),
  byweekday: z.array(z.number().min(0).max(6)).optional(),
  bynweekday: z.array(z.array(z.number())).optional(),
  byhour: z.array(z.number().min(0).max(23)).optional(),
  byminute: z.array(z.number().min(0).max(59)).optional(),
  bysecond: z.array(z.number().min(0).max(59)).optional(),
  byeaster: z.number().optional(),
});

export const rruleInstanceSchema = z.object({
  rule: rruleOptionsSchema,
  dates: z.array(z.date()),
  timeZone: z.string(),
  until: z.date().optional(),
  count: z.number().positive().optional(),
});

/**
 * Generates a RRuleInstance from RRuleOptions
 *
 * @param options - The RRule configuration options
 * @param timeZone - The timezone for date generation
 * @param maxCount - Maximum number of dates to generate (default: 250)
 * @returns RRuleInstance with generated dates
 */
export function generateRRuleInstance(
  options: RRuleOptions,
  timeZone: string,
  maxCount: number = 250,
): RRuleInstance {
  const validatedOptions = rruleOptionsSchema.parse(options);

  const rruleOptions = {
    ...validatedOptions,
    dtstart: validatedOptions.dtstart || new Date(),
    until: validatedOptions.until || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
  } as RRuleOptions;

  const rrule = new RRule(rruleOptions as any);

  const dates = rrule.all((_, i) => (i ?? 0) < maxCount);

  return {
    rule: validatedOptions,
    dates,
    timeZone,
    until: validatedOptions.until,
    count: validatedOptions.count,
  };
}

/**
 * Parses an RRULE string and generates an RRuleInstance
 *
 * @param rruleString - The RRULE string to parse
 * @param timeZone - The timezone for date generation
 * @param maxCount - Maximum number of dates to generate (default: 250)
 * @returns RRuleInstance with generated dates
 */
export function parseRRuleString(
  rruleString: string,
  timeZone: string,
  maxCount: number = 250,
): RRuleInstance {
  const rrule = rrulestr(rruleString);
  const dates = rrule.all((_, i) => (i ?? 0) < maxCount);

  return {
    rule: rrule.options as RRuleOptions,
    dates,
    timeZone,
    until: rrule.options.until || undefined,
    count: rrule.options.count || undefined,
  };
}

export function validateRRuleOptions(options: RRuleOptions): boolean {
  try {
    rruleOptionsSchema.parse(options);
    new RRule(options as any);
    return true;
  } catch {
    return false;
  }
}

export function getNextOccurrence(options: RRuleOptions, fromDate: Date = new Date()): Date | null {
  try {
    const rruleOptions = {
      ...options,
      dtstart: fromDate,
    } as RRuleOptions;
    const rrule = new RRule(rruleOptions as any);
    return rrule.after(fromDate, true);
  } catch {
    return null;
  }
}

export function getOccurrencesBetween(
  options: RRuleOptions,
  startDate: Date,
  endDate: Date,
): Date[] {
  try {
    const rrule = new RRule(options as any);
    return rrule.between(startDate, endDate, true);
  } catch {
    return [];
  }
}
